// @flow

import React from 'react';
import { Bar } from 'react-chartjs-2';
import { I18n, Translate } from 'react-redux-i18n';
import type { WeightedSpendPotentialBL2Bean } from '../../../../../types/apolloType';
import { roundValue } from '../../../../../utils/formatUtils';
import { TOP_LEVEL_CATEGORIES } from '../../../../../const';

type Props = {
  data: WeightedSpendPotentialBL2Bean[],
}

function formatWeightedSpend(value: number): string {
  return `${roundValue(value)}€`;
}

function valuesOverBars(ctx, valuesDataset, overlapDataset) {
  ctx.fillStyle = 'black';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.font = Chart.helpers.fontString(
    10, // font size in px
    Chart.defaults.global.defaultFontStyle,
    Chart.defaults.global.defaultFontFamily,
  );
  const MIN_TEXT_HEIGHT = 15; // minimal height where a text can be placed for this graph
  const TEXT_SPACE = 3; // space between the text and the bar below
  for (let i = 0; i < valuesDataset.data.length; i++) {
    const valuesMetaIndex = Object.keys(valuesDataset._meta)[0];
    const valuesModel = valuesDataset._meta[valuesMetaIndex].data[i]._model;
    const overlapMetaIndex = Object.keys(overlapDataset._meta)[0];
    const overlapModel = overlapDataset._meta[overlapMetaIndex].data[i]._model;
    const y = (valuesModel.y - overlapModel.y > 0 && valuesModel.y - overlapModel.y < MIN_TEXT_HEIGHT + overlapDataset.borderWidth.top)
      ? overlapModel.y - TEXT_SPACE
      : valuesModel.y - TEXT_SPACE;
    ctx.fillText(formatWeightedSpend(valuesDataset.data[i]), valuesModel.x, y);
  }
}

class WeightedSpendPotentialGraph extends React.Component<Props, State> {
  getCaci = () => this.props.data.map(entry => entry.caciSc);

  getAverageCaciCountry = () => this.props.data.map(entry => entry.averageCaciCountry);

  getLabelsCategories = () => this.props.data.map(entry => entry.topLevelCategory);

  getBarColors = () => this.getLabelsCategories()
    .map(label => (TOP_LEVEL_CATEGORIES[label] || TOP_LEVEL_CATEGORIES.default).color);

  getMultiLineLabels = () => (
    this.getLabelsCategories().map((label: string) => {
      if (label.length > 30) {
        const labelWords = label.split(' ');
        const lineSeparator = Math.floor(labelWords.length / 2);
        return [
          labelWords.slice(0, lineSeparator).join(' '),
          labelWords.slice(lineSeparator).join(' '),
        ];
      }
      if (label === 'Personal Care') {
        return 'Health & Beauty';
      }
      return label;
    })
  )

  render() {
    const data = {
      labels: this.getMultiLineLabels(),
      datasets: [
        {
          label: I18n.t('MKT_PAGE.weighted_spend_potential.tooltips_graph.country_average'),
          data: this.getAverageCaciCountry(),
          stack: 1,
          borderWidth: {
            top: 5, bottom: 0, left: 0, right: 0,
          },
          backgroundColor: 'transparent',
          borderColor: 'black',
          minBarLength: 30,
          barPercentage: 1,
          categoryPercentage: 0.8,
        },
        {
          label: I18n.t('MKT_PAGE.weighted_spend_potential.tooltips_graph.weighted_spend'),
          data: this.getCaci(),
          backgroundColor: this.getBarColors(),
          stack: 1,
          minBarLength: 30,
          barPercentage: 1,
          categoryPercentage: 0.8,
        },
      ],
    };

    const options = {
      maintainAspectRatio: true,
      responsive: screen.width > 768,
      legend: {
        display: false,
      },
      layout: { padding: 20 },
      tooltips: {
        callbacks: {
          title: (context) => {
            const labelDisplayed = context[0].label;
            const { additionalInfo } = TOP_LEVEL_CATEGORIES[labelDisplayed]
            || TOP_LEVEL_CATEGORIES.default;
            return additionalInfo.length !== 0 ?
              `${labelDisplayed} - ${additionalInfo}`
              : `${labelDisplayed}`;
          },
          label: (context, data2) => {
            const value = data2.datasets[context.datasetIndex].data[context.index];
            const labelDisplayed = data2.datasets[context.datasetIndex].label;
            return `${labelDisplayed}: ${formatWeightedSpend(value)}`;
          },
          labelColor(tooltipItem, chart) {
            let oldColor = chart.data.datasets[tooltipItem.datasetIndex].backgroundColor;
            oldColor = typeof oldColor === 'string' ? oldColor : oldColor[tooltipItem.index];
            const newColor = (oldColor === 'transparent') ? 'black' : oldColor;
            return {
              borderColor: 'white',
              backgroundColor: newColor,
            };
          },
        },
      },
      scales: {
        xAxes: [
          {
            gridLines: {
              display: false,
              drawBorder: false,
            },
            stacked: true,
          },
        ],
        yAxes: [
          {
            stacked: false,
            ticks: {
              beginAtZero: true,
              callback(value, index, values) {
                if (value === values[values.length - 1]) {
                  return '';
                }
                if (value === values[values.length - 2]) {
                  return `< ${formatWeightedSpend(value)}`;
                }
                return `${formatWeightedSpend(value)}`;
              },
            },
          },
        ],
      },
    };

    const plugins = [{
      afterDatasetsDraw(chartInstance) {
        valuesOverBars(
          chartInstance.chart.ctx,
          chartInstance.data.datasets.filter(cat => cat.backgroundColor !== 'transparent')[0],
          chartInstance.data.datasets.filter(cat => cat.backgroundColor === 'transparent')[0],
        );
      },
    }];

    return (
      <div>
        <div className="weighted-spend-potential-graph">
          <Bar
            height={400}
            width={800}
            options={options}
            data={data}
            plugins={plugins}
          />
        </div>
        <div className="bargraph-legend-container">
          <div className="bargraph-legend">
            {
              this.getBarColors().map((color, index) => (
                <i key={index.toString()} className="fas fa-square branch-level" style={{ color }} />
              ))
            }
            <Translate value="MKT_PAGE.weighted_spend_potential.legend.branch_expectation" />
          </div>
          <div className="bargraph-legend">
            <i className="fas fa-square average" />
            <Translate value="MKT_PAGE.weighted_spend_potential.legend.country_average" />
          </div>
        </div>
      </div>
    );
  }
}

export default WeightedSpendPotentialGraph;
